import React, { useState } from "react";
import { EyeIcon, EyeOffIcon, Spinner } from "./Shared/Icons";
import { GoCheckCircleFill } from "react-icons/go";
import { LoadingButton } from "../../../components/LoadingButton";
import { ErrorAlert } from "../../../components/ErrorAlert";
import { PasswordValidator, checkPasswordRules } from "./PasswordValidator";
import type { useRegister } from "../hooks/useRegister";

interface RegisterViewProps {
  register: ReturnType<typeof useRegister>;
  isDark: boolean;
  onCancel: () => void;
}

export const RegisterView: React.FC<RegisterViewProps> = ({
  register,
  isDark,
  onCancel,
}) => {
  const {
    name,
    email,
    password,
    confirmPassword,
    code,
    error,
    successMessage,
    isLoading,
    step,
    setName,
    setEmail,
    setPassword,
    setConfirmPassword,
    setCode,
    setError,
    setStep,
    handleSignUp,
    handleConfirmSignUp,
    handleResendCode,
    resetForm,
  } = register;

  const [showRegPass, setShowRegPass] = useState(false);
  const [showRegConfirmPass, setShowRegConfirmPass] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [isPassFocused, setIsPassFocused] = useState(false);
  const [isConfirmFocused, setIsConfirmFocused] = useState(false);

  const { isValid: isPasswordValid } = checkPasswordRules(password);

  const handleResend = async () => {
    if (resendCooldown > 0 || isLoading) return;
    await handleResendCode();
    setResendCooldown(60);
    const interval = setInterval(() => {
      setResendCooldown((prev) => {
        if (prev <= 1) { clearInterval(interval); return 0; }
        return prev - 1;
      });
    }, 1000);
  };

  const inputClass = "w-full py-4 px-5 border rounded-xl text-xs font-medium transition-all bg-surface border-border text-text placeholder-text-dim focus:border-accent focus:bg-background";

  return (
    <div>
      <p className="text-xs text-center leading-relaxed mb-3 text-text-dim">
        {step === "SIGNUP"
          ? "Completá los datos para registrarte"
          : `Ingresá el código de verificación enviado a ${email}`}
      </p>

      {error && (
        <ErrorAlert error={error} onDismiss={() => setError("")} className="mb-4" />
      )}

      {successMessage && (
        <div className="flex items-center gap-2 px-4 py-3 bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 rounded-xl text-xs font-semibold mb-4 leading-normal">
          <GoCheckCircleFill style={{ color: "#10B981" }} size={18} className="flex-shrink-0" />
          <span>{successMessage}</span>
        </div>
      )}

      {step === "SIGNUP" ? (
        <form onSubmit={handleSignUp} className="flex flex-col gap-4">
          <input
            type="text"
            placeholder="Nombre Completo"
            value={name}
            onChange={(e) => setName(e.target.value)}
            disabled={isLoading}
            className={inputClass}
            autoFocus
          />

          <input
            type="email"
            placeholder="Correo electrónico"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={isLoading}
            className={inputClass}
          />

          {/* Password */}
          <div className="relative">
            <input
              type={showRegPass ? "text" : "password"}
              placeholder="Contraseña"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onFocus={() => setIsPassFocused(true)}
              onBlur={() => setIsPassFocused(false)}
              disabled={isLoading}
              className={inputClass}
            />
            <button
              type="button"
              onClick={() => setShowRegPass(!showRegPass)}
              className="absolute right-4 top-1/2 -translate-y-1/2 text-text-muted hover:text-accent transition-all text-xs flex items-center justify-center"
            >
              {showRegPass ? <EyeOffIcon /> : <EyeIcon />}
            </button>
          </div>

          {/* Password Validation Rules */}
          {!isConfirmFocused && (isPassFocused || (password.length > 0 && !isPasswordValid)) && (
            <PasswordValidator password={password} isDark={isDark} />
          )}


          {/* Confirm Password */}
          <div className="relative">
            <input
              type={showRegConfirmPass ? "text" : "password"}
              placeholder="Confirmar contraseña"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              onFocus={() => setIsConfirmFocused(true)}
              onBlur={() => setIsConfirmFocused(false)}
              disabled={isLoading}
              className={inputClass}
            />
            <button
              type="button"
              onClick={() => setShowRegConfirmPass(!showRegConfirmPass)}
              className="absolute right-4 top-1/2 -translate-y-1/2 text-text-muted hover:text-accent transition-all text-xs flex items-center justify-center"
            >
              {showRegConfirmPass ? <EyeOffIcon /> : <EyeIcon />}
            </button>
          </div>

          <LoadingButton
            type="submit"
            disabled={!isPasswordValid || !name || !email || !confirmPassword}
            isLoading={isLoading}
            className="w-full py-4 bg-accent hover:bg-accent/80 disabled:opacity-60 text-white font-bold rounded-xl text-sm transition-all shadow-lg shadow-accent/20 active:scale-[0.98]"
          >
            {isLoading ? <span>Registrando...</span> : "Registrarse"}
          </LoadingButton>

          <button
            type="button"
            onClick={onCancel}
            className="text-xs font-semibold text-center mt-0 cursor-pointer transition-all text-text-muted hover:text-accent"
          >
            ¿Ya tenés cuenta? Iniciar sesión
          </button>
        </form>
      ) : (
        /* ── Confirmation step ── */
        <form onSubmit={(e) => handleConfirmSignUp(e, onCancel)} className="flex flex-col gap-4">
          <input
            type="text"
            inputMode="numeric"
            placeholder="Código de verificación"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            disabled={isLoading}
            className={inputClass}
            autoFocus
          />

          {/* Confirm */}
          <LoadingButton
            type="submit"
            isLoading={isLoading}
            className="w-full py-4 bg-accent hover:bg-accent/80 disabled:opacity-60 text-white font-bold rounded-xl text-sm transition-all shadow-lg shadow-accent/20 active:scale-[0.98]"
          >
            {isLoading ? <span>Confirmando...</span> : "Confirmar cuenta"}
          </LoadingButton>

          {/* Resend code */}
          <button
            type="button"
            onClick={handleResend}
            disabled={resendCooldown > 0 || isLoading}
            className={`w-full py-3 rounded-xl text-xs font-semibold border transition-all active:scale-[0.98] ${
              resendCooldown > 0 || isLoading
                ? isDark
                  ? "border-white/10 bg-white/[0.02] text-white/25 cursor-not-allowed"
                  : "border-gray-200 bg-gray-100 text-gray-300 cursor-not-allowed"
                : isDark
                  ? "border-white/10 bg-white/[0.03] text-white/60 hover:bg-white/[0.07] hover:text-white"
                  : "border-border bg-surface text-text-dim hover:bg-surface-hover hover:text-accent"
            }`}
          >
            {resendCooldown > 0
              ? `Reenviar código (${resendCooldown}s)`
              : "Reenviar código"}
          </button>

          {/* Back */}
          <button
            type="button"
            onClick={() => {
              if (window.history.state?.authStep === "CONFIRM_SIGNUP") {
                window.history.back();
              } else {
                resetForm();
              }
            }}
            className="text-xs font-semibold text-center mt-1 cursor-pointer transition-all text-text-muted hover:text-accent"
          >
            Volver al registro
          </button>
        </form>
      )}
    </div>
  );
};
